import { ComponentProps } from 'react';
import clsx from 'clsx';
import Transfer from 'antd/es/transfer';
import { useTranslation } from 'react-i18next';

import './OpTransfer.scss';

interface OpTransferProps extends ComponentProps<typeof Transfer> {
  testId?: string;
}

/**
 * OpTransfer is a wrapper around Ant Design's Transfer component.
 * It provides a dual-list interface for moving items between two lists (available and existing).
 * 
 * Use this component when users need to select items from one list and move them to another,
 * such as assigning permissions, selecting users, or configuring settings. Includes translated
 * "AVAILABLE" and "EXISTING" titles by default.
 * 
 * @example
 * <OpTransfer
 *   dataSource={items}
 *   targetKeys={selectedKeys}
 *   onChange={handleChange}
 *   render={(item) => item.title}
 * />
 * 
 */
export const OpTransfer = ({
  className,
  testId = 'op-transfer',
  titles,
  ...elementProps
}: OpTransferProps) => {
  const { t } = useTranslation();

  const newTitle = titles ?? [
    <strong key="available">{t('AVAILABLE')}</strong>,
    <strong key="existing">{t('EXISTING')}</strong>,
  ];

  return (
    // Currently antd doesn't support data-testid being directly added to the component
    <div data-testid={testId}>
      <Transfer
        className={clsx('op-transfer', className)}
        titles={newTitle}
        {...elementProps}
      />
    </div>
  );
};
